import { Operation } from '@apollo/client'
import * as Sentry from '@sentry/react'
import { equalsIgnoreCase } from '../../helpers/stringUtils'
import { GRAPHQL_ERROR_MESSAGE } from '../../monitoring/constants'
import { RequestErrorType } from '../../graphql/utils/parseError'

interface GraphQLErrorTelemetryError {
  message?: string
  path?: readonly unknown[]
  extensions?: {
    code?: unknown
    status?: unknown
    originalError?: { message?: unknown } | null
  } | null
}

type GraphQLErrorTelemetryOperation = Pick<Operation, 'operationName' | 'variables'>

const ORGANIZATION_NOT_FOUND_MESSAGE = 'No Organization found'
const NOT_FOUND_MESSAGE = 'Not Found'
const SUBDOMAIN_LOOKUP_PATH = 'organizationBySubdomain'
const ORGANIZATION_NOT_FOUND_HTTP_STATUS = 404

// Expected user-facing AI Reports guidance (unsupported/ambiguous prompt), not actionable
// errors. Suppressed from Sentry; the operationName guard avoids matching similar text elsewhere.
const GENERATE_REPORT_OPERATION = 'GenerateReport'
const GENERATE_REPORT_EXPECTED_MESSAGE_PREFIXES = [
  'Unable to generate this report. Please try rephrasing',
  'Failed to understand request:',
]

const BAD_REQUEST_CODE = 'BAD_REQUEST'
// Operations where a duplicate-email validation failure is expected user error,
// not an actionable bug. These admin/vendor create flows already surface the
// error to the user via `handleAdminFormError`, so it should not also reach Sentry.
const DUPLICATE_EMAIL_SUPPRESSED_OPERATIONS = new Set([
  'createUser',
  'createExternalOrganization',
])

// Extract the human-readable validation message(s) from a GraphQL error.
// NestJS' ValidationPipe nests them at `extensions.originalError.message`
// (a string or string[]). We surface them at a shallow Sentry `extra` key so
// they aren't truncated to "[Array]" by Sentry's default `normalizeDepth`.
export const extractValidationMessages = (
  err: GraphQLErrorTelemetryError | undefined,
): string | undefined => {
  const raw = err?.extensions?.originalError?.message
  if (Array.isArray(raw)) {
    const messages = raw.filter((message): message is string => typeof message === 'string')
    return messages.length > 0 ? messages.join('; ') : undefined
  }
  return typeof raw === 'string' ? raw : undefined
}

// Visitors hitting a subdomain that doesn't resolve to an organization (decommissioned
// tenant, stale DNS, typo, bot traffic) trigger a 404 / "No Organization found" on every
// subdomain-scoped query/mutation. This is expected behavior, not an actionable error, so
// we record a breadcrumb instead of flooding Sentry. The `subdomain` variable guard keeps
// us from suppressing a genuine "No Organization found" raised outside the subdomain-lookup flow.
export const isOrganizationNotFoundError = (
  err: GraphQLErrorTelemetryError | undefined,
  operation: GraphQLErrorTelemetryOperation,
): boolean => {
  if (operation.variables?.subdomain == null) return false
  if (equalsIgnoreCase(err?.message, ORGANIZATION_NOT_FOUND_MESSAGE)) return true
  return (
    equalsIgnoreCase(err?.message, NOT_FOUND_MESSAGE) &&
    (equalsIgnoreCase(err?.path?.[0], SUBDOMAIN_LOOKUP_PATH) ||
      err?.extensions?.status === ORGANIZATION_NOT_FOUND_HTTP_STATUS)
  )
}

export const isExpectedGenerateReportError = (
  err: GraphQLErrorTelemetryError | undefined,
  operation: GraphQLErrorTelemetryOperation,
): boolean => {
  if (operation.operationName !== GENERATE_REPORT_OPERATION) return false
  const message = err?.message
  if (typeof message !== 'string') return false
  return GENERATE_REPORT_EXPECTED_MESSAGE_PREFIXES.some((prefix) => message.startsWith(prefix))
}

// FORBIDDEN means backend authorization correctly denied the operation — expected behavior,
// not an actionable error. Record a breadcrumb instead of flooding Sentry.
export const isAuthorizationDeniedError = (err: GraphQLErrorTelemetryError | undefined): boolean =>
  err?.extensions?.code === RequestErrorType.FORBIDDEN

// Admin/vendor create flows submitting an email that already exists trigger a
// `BAD_REQUEST` whose validation message reads "A user with email '…' already exists.
// Email must be unique." This is expected user-facing validation (handled on the form),
// so we record a breadcrumb instead of capturing it. Scoped to the create operations
// above so genuine failures on other operations still reach Sentry.
export const isDuplicateEmailValidationError = (
  err: GraphQLErrorTelemetryError | undefined,
  operation: GraphQLErrorTelemetryOperation,
): boolean => {
  if (!DUPLICATE_EMAIL_SUPPRESSED_OPERATIONS.has(operation.operationName ?? '')) return false
  if (err?.extensions?.code !== BAD_REQUEST_CODE) return false
  const messages = extractValidationMessages(err)?.toLowerCase()
  if (messages == null) return false
  return messages.includes('already exists') && messages.includes('email must be unique')
}

// PII-free signature of the error's cause, so distinct failures on the same
// operation+code get separate Sentry groups. Prefers the nested validation message
// (the top-level BAD_REQUEST message is a generic "Bad Request Exception").
export const buildErrorFingerprintSignature = (
  err: GraphQLErrorTelemetryError | undefined,
): string => {
  const raw = extractValidationMessages(err) ?? (typeof err?.message === 'string' ? err.message : '')
  // Strip dynamic/PII bits (quoted values, emails, numbers) so one class = one signature.
  const normalized = raw
    .replace(/'[^']*'/g, "'?'")
    .replace(/"[^"]*"/g, '"?"')
    .replace(/\S+@\S+/g, '?')
    .replace(/\d+/g, '#')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .slice(0, 100)
  return normalized || 'unknown'
}

export const emitGraphQLErrorTelemetry = (
  err: GraphQLErrorTelemetryError | undefined,
  operation: GraphQLErrorTelemetryOperation,
): void => {
  if (isExpectedGenerateReportError(err, operation)) {
    Sentry.addBreadcrumb({
      category: 'graphql',
      message: 'Expected GenerateReport LLM failure - skipping Sentry capture',
      level: 'info',
      data: {
        operation: operation.operationName,
        errorMessage: err?.message,
      },
    })
    return
  }

  if (isOrganizationNotFoundError(err, operation)) {
    Sentry.addBreadcrumb({
      category: 'graphql',
      message: 'Org-not-found for subdomain - skipping Sentry capture',
      level: 'info',
      data: {
        operation: operation.operationName,
        subdomain: operation.variables?.subdomain,
        errorMessage: err?.message,
        errorPath: err?.path,
      },
    })
    return
  }

  if (isAuthorizationDeniedError(err)) {
    Sentry.addBreadcrumb({
      category: 'graphql',
      message: 'Authorization denied (FORBIDDEN) - skipping Sentry capture',
      level: 'info',
      data: {
        operation: operation.operationName,
        errorMessage: err?.message,
        errorPath: err?.path,
      },
    })
    return
  }

  if (isDuplicateEmailValidationError(err, operation)) {
    Sentry.addBreadcrumb({
      category: 'graphql',
      message: 'Duplicate-email validation on create - skipping Sentry capture',
      level: 'info',
      data: {
        operation: operation.operationName,
        errorMessage: err?.message,
        validationMessages: extractValidationMessages(err),
      },
    })
    return
  }

  Sentry.withScope((scope) => {
    scope.setLevel('info')
    scope.setFingerprint([
      'graphql-error',
      operation.operationName,
      String(err?.extensions?.code ?? 'unknown'),
      buildErrorFingerprintSignature(err),
    ])
    Sentry.captureException(new Error(GRAPHQL_ERROR_MESSAGE), {
      extra: {
        operation: operation.operationName,
        variables: operation.variables,
        errorMessage: err?.message,
        errorCode: err?.extensions?.code,
        errorPath: err?.path,
        // Shallow key so it survives Sentry's normalizeDepth (the nested
        // errorExtensions.originalError.message is otherwise dropped as "[Array]").
        validationMessages: extractValidationMessages(err),
        errorExtensions: err?.extensions,
      },
    })
  })
}
